import * as _ from 'lodash';
import { ArticleSource, Category, DetailedSize, StandardizedProduct } from './article-matrix.types';
import { ArticleDetails } from '../../components/smart-replenishment/articles/article-details';
import { ArticleCardV4 } from '../../components/smart-replenishment/articles/article-card-v4/article-card-v4.types';
import { ArticleCard } from 'app/smart-replenishment/components/smart-replenishment';
import {
    getBrandStockAvailableQuantity,
    mapBrandStockToObject
} from 'app/smart-replenishment/helpers/smart-replenishment.utils';

export function standardizeArticle(
    mode: ArticleSource,
    article: PossibleArticleSources,
    storeGln?: string
): { category: Category; products: StandardizedProduct[] } {
    let products: StandardizedProduct[] = [];
    let category = Category.Other;

    if (isSourceArticleDetails(mode, article)) {
        if (article.products) {
            category = _.get(article, 'products[0].categoryKey', _.get(article, 'categoryKey', Category.Other));
            products = article.products.map((product) => {
                const brandStock = mapBrandStockToObject(product.brandStock);

                return {
                    gtin: product.gtin,
                    size: standardizeSize(product),
                    selectedQuantity: Number(product.suggestedQuantity || 0),
                    availableQuantity: getBrandStockAvailableQuantity(brandStock),
                    retailerStock: Number(product.retailerStock || 0),
                    // @ts-expect-error need to update type later
                    soldQuantity: Number(product.soldLast30Days || 0),
                    brandStock: {
                        available: brandStock.available,
                        backorder: {
                            available: brandStock.backorder?.available || 0,
                            deliveryFrom: brandStock.backorder?.deliveryFrom || '',
                            deliveryTo: brandStock.backorder?.deliveryTo || ''
                        }
                    }
                };
            });
        } else if (article.sizes && storeGln) {
            category = _.get(article, 'sizes[0].categoryKey', Category.Other);
            products = article.sizes.map((product) => {
                const brandStock = mapBrandStockToObject(_.get(product, `autoRestock.${storeGln}.brandStock`));
                const retailerStock = _.get(product, `autoRestock.${storeGln}.retailerStock`) || 0;
                const soldQuantity = _.get(product, `autoRestock.${storeGln}.soldLast30Days`) || 0;

                return {
                    gtin: product.gtin,
                    size: standardizeSize(product),
                    selectedQuantity: Number(_.get(product, `autoRestock.${storeGln}.targetStock`) || 0),
                    availableQuantity: getBrandStockAvailableQuantity(brandStock),
                    brandStock,
                    retailerStock,
                    soldQuantity
                };
            });
        }
    } else if (isSourceArticleCardV4(mode, article)) {
        category = _.get(article, 'sizes[0].categoryKey', Category.Other);
        products = article.sizes.map((product) => {
            const brandStock = mapBrandStockToObject(product.brandStock);

            return {
                gtin: product.gtin,
                size: standardizeSize(product),
                selectedQuantity: Number(product.targetQuantity || 0),
                availableQuantity: getBrandStockAvailableQuantity(brandStock),
                brandStock: {
                    available: brandStock.available,
                    backorder: {
                        available: brandStock.backorder?.available || 0,
                        deliveryFrom: brandStock.backorder?.deliveryFrom || '',
                        deliveryTo: brandStock.backorder?.deliveryTo || ''
                    }
                }
            };
        });
    } else if (isSourceArticleCardLegacy(mode, article)) {
        category = _.get(article, 'gtins[0].categoryKey', Category.Other);

        products = article.gtins.map((product) => {
            const brandStock = mapBrandStockToObject(product.brandStock);

            return {
                gtin: product.gtin,
                size: standardizeSize(product),
                selectedQuantity: Number(product.targetQuantity || 0),
                availableQuantity: getBrandStockAvailableQuantity(brandStock),
                retailerStock: product.retailerStock || 0,
                soldQuantity: product.soldQuantity || 0,
                brandStock: {
                    available: brandStock.available,
                    backorder: {
                        available: brandStock.backorder?.available || 0,
                        deliveryFrom: brandStock.backorder?.deliveryFrom || '',
                        deliveryTo: brandStock.backorder?.deliveryTo || ''
                    }
                }
            };
        });
    }

    return { category, products };
}

type PossibleArticleSources = ArticleDetails | ArticleCardV4 | ArticleCard;

function isSourceArticleDetails(mode: ArticleSource, article: PossibleArticleSources): article is ArticleDetails {
    return mode === ArticleSource.Details;
}

function isSourceArticleCardV4(mode: ArticleSource, article: PossibleArticleSources): article is ArticleCardV4 {
    return mode === ArticleSource.CardV4;
}

function isSourceArticleCardLegacy(mode: ArticleSource, article: PossibleArticleSources): article is ArticleCard {
    return mode === ArticleSource.CardLegacy;
}

function standardizeSize<T extends { size: string; detailedSize?: DetailedSize }>(product: T): string {
    const fallbackSize: string = product.size;
    const detailedSize: DetailedSize = product.detailedSize;

    if (detailedSize) {
        const { size, sizeType, subSize, subSizeType } = detailedSize;

        if (size && subSize) {
            // THE USUAL PATTERN IS WIDTH/LENGTH 28/30 W28/L30
            const isExpectedPattern = sizeType.toLowerCase() === 'width' && subSizeType.toLowerCase() === 'length';

            if (isExpectedPattern) {
                return `${size}/${subSize}`;
            } else {
                return `${subSize}/${size}`;
            }
        } else {
            return size || subSize;
        }
    }

    return fallbackSize;
}
